import { useEffect, useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../utils/api";
import { usePraxis } from "../context/PraxisContext";

/**
 * Zwei separate Admin-Bereiche:
 *   - <ContactManager />  Kontaktdaten (admin only)
 *   - <HoursManager />    Sprechzeiten (admin + editor)
 */
export function ContactManager({ readOnly = false }) {
  const { refresh } = usePraxis();
  return (
    <div data-testid="contact-manager">
      <ContactForm onSaved={refresh} readOnly={readOnly} />
    </div>
  );
}

export function HoursManager({ readOnly = false }) {
  const { refresh } = usePraxis();
  return (
    <div data-testid="hours-manager">
      <HoursList onChanged={refresh} readOnly={readOnly} />
    </div>
  );
}

export default ContactManager;

/* -------- Kontakt-Daten -------- */
function ContactForm({ onSaved, readOnly = false }) {
  const [values, setValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.getSettings();
      setLoading(false);
      if (data && data.ok) setValues(data.settings || {});
      else setError("Konnte Kontaktdaten nicht laden.");
    })();
  }, []);

  const set = (k, v) => setValues({ ...values, [k]: v });

  const save = async () => {
    setError(""); setMessage("");
    setSaving(true);
    const { data } = await api.updateSettings(values || {});
    setSaving(false);
    if (data && data.ok) {
      setMessage("Kontaktdaten gespeichert.");
      onSaved && onSaved();
    } else {
      setError("Speichern fehlgeschlagen.");
    }
  };

  if (loading) return <p>Lade Kontaktdaten…</p>;
  if (!values) return <p className="admin-error">Kontaktdaten nicht verfügbar.</p>;

  return (
    <div className="admin-form" data-testid="contact-form" style={{ marginBottom: "2rem" }}>
      <h2 className="admin-list-title" style={{ marginTop: 0 }}>Kontaktdaten</h2>
      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      <div className="admin-two-col">
        <label className="admin-label">
          Praxis-Name
          <input type="text" value={values.name || ""} onChange={(e) => set("name", e.target.value)}
                 maxLength={120} data-testid="settings-name-input"/>
        </label>
        <label className="admin-label">
          Fachrichtung
          <input type="text" value={values.role || ""} onChange={(e) => set("role", e.target.value)}
                 maxLength={200} data-testid="settings-role-input"/>
        </label>
      </div>

      <div className="admin-two-col">
        <label className="admin-label">
          Straße + Hausnummer
          <input type="text" value={values.street || ""} onChange={(e) => set("street", e.target.value)}
                 maxLength={120} data-testid="settings-street-input"/>
        </label>
        <label className="admin-label">
          PLZ + Ort
          <div style={{ display: "grid", gridTemplateColumns: "6rem 1fr", gap: "0.5rem" }}>
            <input type="text" value={values.zip || ""} onChange={(e) => set("zip", e.target.value)}
                   maxLength={10} placeholder="PLZ" data-testid="settings-zip-input"/>
            <input type="text" value={values.city || ""} onChange={(e) => set("city", e.target.value)}
                   maxLength={80} placeholder="Ort" data-testid="settings-city-input"/>
          </div>
        </label>
      </div>

      <div className="admin-two-col">
        <label className="admin-label">
          Telefon (Anzeige-Format)
          <input type="text" value={values.phone || ""} onChange={(e) => set("phone", e.target.value)}
                 placeholder="06206 96 33 20" data-testid="settings-phone-input"/>
        </label>
        <label className="admin-label">
          Telefon (Wählformat, für tel:-Links)
          <input type="text" value={values.phone_raw || ""} onChange={(e) => set("phone_raw", e.target.value)}
                 placeholder="+49 6206 963320" data-testid="settings-phoneraw-input"/>
        </label>
      </div>

      <div className="admin-two-col">
        <label className="admin-label">
          Fax (Anzeige)
          <input type="text" value={values.fax || ""} onChange={(e) => set("fax", e.target.value)}
                 placeholder="06206 70 74 51" data-testid="settings-fax-input"/>
        </label>
        <label className="admin-label">
          Fax (Wählformat)
          <input type="text" value={values.fax_raw || ""} onChange={(e) => set("fax_raw", e.target.value)}
                 placeholder="+49 6206 707451" data-testid="settings-faxraw-input"/>
        </label>
      </div>

      <div className="admin-two-col">
        <label className="admin-label">
          E-Mail
          <input type="email" value={values.email || ""} onChange={(e) => set("email", e.target.value)}
                 data-testid="settings-email-input"/>
        </label>
        <label className="admin-label">
          Website (Anzeige)
          <input type="text" value={values.web || ""} onChange={(e) => set("web", e.target.value)}
                 placeholder="www.praxis-scheer.de" data-testid="settings-web-input"/>
        </label>
      </div>

      <label className="admin-label">
        Website (voller Link mit https://)
        <input type="url" value={values.web_url || ""} onChange={(e) => set("web_url", e.target.value)}
               placeholder="https://www.praxis-scheer.de" data-testid="settings-weburl-input"/>
      </label>

      <div className="admin-actions-row">
        <button type="button" onClick={save} className="admin-btn admin-btn-primary" disabled={saving || readOnly} data-testid="settings-save">
          {saving ? "Speichere…" : "Kontaktdaten speichern"}
        </button>
      </div>
    </div>
  );
}

/* -------- Sprechzeiten -------- */
function HoursList({ onChanged, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const reload = async () => {
    setLoading(true);
    const { data } = await api.listHours();
    setLoading(false);
    if (data && data.ok) setItems(data.items || []);
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => setEditing({ id: null, day: "", time: "" });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => { setEditing(null); setError(""); setMessage(""); };

  const save = async (item) => {
    setError(""); setMessage("");
    if (!item.day.trim() || !item.time.trim()) {
      setError("Bitte Tag und Zeit ausfüllen.");
      return;
    }
    const payload = { day: item.day.trim(), time: item.time };
    const { data } = item.id
      ? await api.updateHours(item.id, payload)
      : await api.createHours(payload);
    if (data && data.ok) {
      setMessage(item.id ? "Sprechzeit aktualisiert." : "Sprechzeit angelegt.");
      setEditing(null);
      await reload();
      onChanged && onChanged();
    } else {
      setError((data && data.message) || "Speichern fehlgeschlagen.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`„${it.day}“ wirklich löschen?`)) return;
    const { data } = await api.deleteHours(it.id);
    if (data && data.ok) { setMessage("Sprechzeit gelöscht."); await reload(); onChanged && onChanged(); }
  };

  const onDragEnd = async (e) => {
    if (readOnly) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    await api.reorderHours(next.map((i) => i.id));
    onChanged && onChanged();
  };

  return (
    <div data-testid="hours-list">
      <div className="admin-list-header">
        <h2 className="admin-list-title">Sprechzeiten ({items.length})</h2>
        <button type="button" onClick={startNew} className="admin-btn admin-btn-primary" disabled={readOnly} data-testid="hours-new">
          + Neue Zeile
        </button>
      </div>

      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {editing && (
        <HoursForm item={editing} onCancel={cancel} onSave={save} readOnly={readOnly} />
      )}

      {!editing && (loading ? <p>Lade…</p> : items.length === 0 ? (
        <p className="admin-empty">Noch keine Sprechzeiten hinterlegt.</p>
      ) : (
        <>
          <p className="admin-align-hint" style={{ margin: "0 0 0.75rem" }}>
            {readOnly
              ? "Reihenfolge ist im Lesemodus nicht änderbar."
              : "Reihenfolge per Drag-and-Drop änderbar. Zeilenumbrüche im Zeit-Feld werden auf der Startseite angezeigt."}
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="team-list" data-testid="hours-items">
                {items.map((it) => (
                  <SortableHoursRow key={it.id} item={it} onEdit={() => startEdit(it)} onDelete={() => remove(it)} readOnly={readOnly} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      ))}
    </div>
  );
}

function SortableHoursRow({ item, onEdit, onDelete, readOnly = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: readOnly });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style} className="team-list-item hours-list-item" data-testid={`hours-item-${item.id}`}>
      <span className="team-drag-handle" {...attributes} {...listeners} title={readOnly ? "Lesemodus" : "Ziehen zum Sortieren"} style={readOnly ? { cursor: "not-allowed", opacity: 0.4 } : undefined}>
        <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor" aria-hidden="true">
          <circle cx="4" cy="4" r="1.5"/><circle cx="10" cy="4" r="1.5"/>
          <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/>
          <circle cx="4" cy="16" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
        </svg>
      </span>
      <div className="team-info">
        <div className="team-name">{item.day}</div>
        <div className="team-position" style={{ whiteSpace: "pre-line" }}>{item.time}</div>
      </div>
      <div className="team-actions">
        <button type="button" onClick={onEdit} className="admin-btn admin-btn-ghost" data-testid={`hours-edit-${item.id}`}>{readOnly ? "Ansehen" : "Bearbeiten"}</button>
        <button type="button" onClick={onDelete} className="admin-btn admin-btn-danger" disabled={readOnly} data-testid={`hours-delete-${item.id}`}>Löschen</button>
      </div>
    </li>
  );
}

function HoursForm({ item, onCancel, onSave, readOnly = false }) {
  const [current, setCurrent] = useState(item);
  const [saving, setSaving] = useState(false);
  const doSave = async () => { setSaving(true); await onSave(current); setSaving(false); };
  return (
    <div className="admin-form" data-testid="hours-form" style={{ marginBottom: "1.5rem" }}>
      <h3 className="admin-list-title" style={{ fontSize: "1.1rem" }}>
        {item.id ? "Sprechzeit bearbeiten" : "Neue Sprechzeit"}
      </h3>
      <div className="admin-two-col">
        <label className="admin-label">
          Tag
          <input type="text" value={current.day}
                 onChange={(e) => setCurrent({ ...current, day: e.target.value })}
                 placeholder="z. B. Montag" maxLength={40}
                 data-testid="hours-day-input"/>
        </label>
        <label className="admin-label">
          Zeit (Zeilenumbrüche mit Enter möglich)
          <textarea value={current.time} rows={2}
                    onChange={(e) => setCurrent({ ...current, time: e.target.value })}
                    placeholder="z. B. 08:00 – 12:30 Uhr" maxLength={200}
                    data-testid="hours-time-input"/>
        </label>
      </div>
      <div className="admin-actions-row">
        <button type="button" onClick={doSave} className="admin-btn admin-btn-primary" disabled={saving || readOnly} data-testid="hours-save">
          {saving ? "Speichere…" : "Speichern"}
        </button>
        <button type="button" onClick={onCancel} className="admin-btn admin-btn-ghost" data-testid="hours-cancel">
          {readOnly ? "Zurück" : "Abbrechen"}
        </button>
      </div>
    </div>
  );
}
